import { FeatureFlag, isEnabled } from "@client/monolith/legacy/services/features";
import { AksInsertCtaButton, PLUGIN_KEY as InsertCtaPluginKey } from "./aks-insert-cta-plugin";
import { AksInsertLinkButton, PLUGIN_KEY as InsertLinkPluginKey } from "./aks-insert-link-plugin";
import { AksInsertImageButton, PLUGIN_KEY as InsertImagePluginKey } from "./aks-insert-image-plugin";
import { AksInsertProductCardButton, PLUGIN_KEY as InsertProductCardPluginKey } from "./aks-insert-product-card-plugin";
import { AksInsertAiContentButton, PLUGIN_KEY as InsertAiContentPluginKey } from "./aks-insert-ai-content-plugin";
import { computed } from "vue";
import { EditorOptions } from "tinymce";

interface AksPlugin {
    featureFlag: FeatureFlag;
    pluginKey: string;
    toolbarItem: string;
    editorConfig?: Record<string, any>;
}

export default function useAksPluginManager(namespace: string) {
    const optionalPlugins: AksPlugin[] = [
        {
            featureFlag: FeatureFlag.CampaignEditorPluginCTA,
            pluginKey: InsertCtaPluginKey,
            toolbarItem: AksInsertCtaButton.InsertCta,
        },
        {
            featureFlag: FeatureFlag.CampaignEditorPluginLink,
            pluginKey: InsertLinkPluginKey,
            toolbarItem: AksInsertLinkButton.InsertLink,
        },
        {
            featureFlag: FeatureFlag.CampaignEditorPluginImage,
            pluginKey: InsertImagePluginKey,
            toolbarItem: AksInsertImageButton.InsertImage,
        },
        {
            featureFlag: FeatureFlag.CampaignEditorPluginProductCard,
            pluginKey: InsertProductCardPluginKey,
            toolbarItem: AksInsertProductCardButton.InsertProductCard,
        },
        {
            featureFlag: FeatureFlag.CampaignEditorPluginAiContent,
            pluginKey: InsertAiContentPluginKey,
            toolbarItem: AksInsertAiContentButton.InsertAiContent,
        }
    ];

    const aksPluginConfig = computed(() => {
        const pluginKeys = [];
        const toolbarItems = [];

        for (const { featureFlag, pluginKey, toolbarItem } of optionalPlugins) {
            if (isEnabled(featureFlag)) {
                pluginKeys.push(pluginKey);
                toolbarItems.push(toolbarItem);
            }
        }

        return {
            // `-`: Prefixed hyphen prevents TinyMCE from trying to auto-load the custom plugins, necessary
            // since we manually manage registration (see https://www.tiny.cloud/docs/tinymce/5/apis/tinymce.addonmanager/#load).
            // `namespace`: necessary to avoid conflicts with other TinyMCE instances that might be in the same page.
            plugins: pluginKeys.map(key => `-${namespace}_${key}`),
            toolbarItems: toolbarItems.join(' '),
        };
    });

    const injectAksPluginConfig = (coreConfig: Partial<EditorOptions>) => {
        return {
            ...coreConfig,
            plugins: [
                ...coreConfig.plugins,
                ...aksPluginConfig.value.plugins
            ],
            toolbar: [
                ...coreConfig.toolbar as string[],
                aksPluginConfig.value.toolbarItems
            ],
            // Necessary to prevent a double-rendering bug when a custom plugin inserts some content.
            content_style: `${coreConfig.content_style} body { .mce-offscreen-selection { display: none; } }`,

            // Disables ability to paste links over existing text (since we cannot easily validate them).
            smart_paste: false,
        };
    };

    return {
        injectAksPluginConfig
    };
}
